import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Download, Zap, Columns3, Search, Plus, ChevronLeft } from 'lucide-react';
import Modal from '@mui/material/Modal';

import api from '../../services/api';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import TextInput from '../../components/ui/TextInput';
import DateTimePicker from '../../components/ui/DateTimePicker';
import PersonCell from '../../components/ui/PersonCell';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { formatPhone } from '../../utils/phone';
import isInsidePortalLayer from '../../utils/isInsidePortalLayer';

// ─── Filtre şeridi ───────────────────────────────────────────────
// Eski tasarım bir sorgu kurucusuydu: satır başına "hangi alan" seçici + değer
// + Uygula. Kullanıcı geri bildirimi üzerine (2026-08-11) tek satırlık okunur
// bir cümleye çevrildi — durum segmenti, uygulanan filtreler çip olarak, canlı
// sayaç ve araç butonları aynı şeritte. "Uygula" adımı kaldırıldı: seçim anında
// geçerli. Bu yalnız estetik değil — taslak filtre ile uygulanmış filtrenin
// ayrışması (Excel çıktısının tablodan farklı çıkması) bu adımdan doğuyordu.

const Container = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const Bar = styled.div`
  background: #fff;
  border: 1.5px solid #E0E4E8;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  position: relative;
  z-index: 10;
`;

const Segment = styled.div`
  display: inline-flex;
  background: #EEF2F4;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  flex: none;
`;

const SegmentBtn = styled.button`
  font-family: inherit;
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  border: 0;
  background: ${({ $active }) => ($active ? '#fff' : 'none')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(0, 66, 66, 0.12)' : 'none')};
  color: ${({ $active, theme }) => ($active ? theme.colors.text.primary : '#8A9BA0')};
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;

  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const Divider = styled.span`
  width: 1px;
  align-self: stretch;
  background: #E0E4E8;
`;

const Chips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1.5px solid rgba(2, 145, 239, 0.35);
  background: rgba(2, 145, 239, 0.07);
  border-radius: 999px;
  overflow: hidden;
`;

const ChipMain = styled.button`
  font-family: inherit;
  font-size: 12.5px;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 5px 4px 5px 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  display: inline-flex;
  gap: 5px;
  align-items: baseline;

  &:hover { background: rgba(2, 145, 239, 0.1); }

  .k { color: #4C6F73; }
  .v { font-weight: ${({ theme }) => theme.metrics.font.weight.bold}; }
`;

const ChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 5px 9px 5px 5px;
  color: #6E8B8E;

  &:hover { color: #E53935; }
`;

const AddFilterBtn = styled.button`
  font-family: inherit;
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  border: 1.5px dashed #C2CFD4;
  background: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 5px 13px;
  color: #4C6F73;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.main};
    color: ${({ theme }) => theme.colors.primary.main};
    background: rgba(2, 145, 239, 0.05);
  }
`;

const BarRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
`;

// Canlı sayaç: filtrenin işe yarayıp yaramadığını görünür kılar. Sessizce
// uygulanmayan bir filtre (yanlış sorgu-parametresi adı) burada anında belli olur.
const Count = styled.span`
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: #4C6F73;
  white-space: nowrap;

  b {
    color: ${({ $filtered, theme }) => ($filtered ? '#0277BD' : theme.colors.text.primary)};
    font-weight: ${({ theme }) => theme.metrics.font.weight.extraBold};
    font-size: 14.5px;
  }
`;

const ClearBtn = styled.button`
  font-family: inherit;
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  border: 0;
  background: none;
  cursor: pointer;
  color: #6E8B8E;
  padding: 4px 2px;

  &:hover { color: #E53935; text-decoration: underline; }
`;

// ─── Alan/değer seçme kutusu ─────────────────────────────────────

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 12px;
  width: 320px;
  max-width: calc(100% - 24px);
  background: #fff;
  border: 1.5px solid #E0E4E8;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 66, 66, 0.16);
  z-index: 200;
  overflow: hidden;
`;

const PopHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 11px;
  border-bottom: 1px solid #EDF1F3;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PopBack = styled.button`
  display: inline-flex;
  align-items: center;
  border: 0;
  background: none;
  cursor: pointer;
  color: #6E8B8E;
  padding: 2px;

  &:hover { color: ${({ theme }) => theme.colors.primary.main}; }
`;

const PopSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 11px;
  border-bottom: 1px solid #EDF1F3;
  color: #8A9BA0;

  input {
    font-family: inherit;
    font-size: 13px;
    border: 0;
    outline: 0;
    width: 100%;
    background: none;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const PopList = styled.div`
  max-height: 260px;
  overflow-y: auto;
  padding: 5px 0;
`;

const PopGroup = styled.div`
  font-size: 10.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8A9BA0;
  padding: 8px 13px 4px;
`;

const PopItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 13px;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 7px 13px;
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover, &:focus-visible { background: rgba(2, 145, 239, 0.08); outline: 0; }
`;

const PopEmpty = styled.div`
  padding: 16px 13px;
  font-size: 13px;
  color: #8A9BA0;
`;

const PopForm = styled.form`
  padding: 11px;
  display: flex;
  gap: 7px;
  align-items: flex-start;

  > *:first-child { flex: 1; min-width: 0; }
`;

// ─── Kolon seçici ────────────────────────────────────────────────

const ColModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  min-width: 380px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 40px rgba(0, 66, 66, 0.2);
  outline: none;
`;

const ColModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ColCheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 13px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ColModalActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  justify-content: flex-end;
`;

// ─── Hızlı aksiyon menüsü ────────────────────────────────────────

const DropdownWrapper = styled.div`
  position: relative;
  /* inline-flex: Tooltip sarmalayıcısı inline-block; satır tabanı boşluğu
     butonu şeritteki diğer ikonlarla hizasından kaydırıyordu. */
  display: inline-flex;
  align-items: center;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  /* Sağa değil sola yaslı: right:0 menüyü sola doğru büyütüyor, sabit kenar
     çubuğunun altına ve Main'in overflow-x kırpma bölgesine sokuyordu. */
  right: 0;
  background: #fff;
  border: 1.5px solid #E0E4E8;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 66, 66, 0.12);
  z-index: 200;
  min-width: 220px;
  overflow: hidden;
`;

const DropdownItem = styled.div`
  padding: 10px 14px;
  font-size: 13px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:hover { background: rgba(2, 145, 239, 0.06); }
  &:not(:last-child) { border-bottom: 1px solid #F0F2F5; }
`;

// Aksiyonun kapsamını çekmecenin içinde gösterir — kullanıcı kaç kişiye
// uygulandığını onaylamadan ÖNCE görmeli.
const TargetSummary = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  background: ${({ $warn }) => ($warn ? 'rgba(229, 57, 53, 0.06)' : 'rgba(2, 145, 239, 0.06)')};
  border: 1px solid ${({ $warn }) => ($warn ? 'rgba(229, 57, 53, 0.3)' : 'rgba(2, 145, 239, 0.25)')};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TargetWarning = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #C62828;
`;

// ─── Filtre alanları ─────────────────────────────────────────────

// Anahtarlar backend'in `[FromQuery(Name = ...)]` bağlamalarının BİREBİR aynısı
// olmalı. ASP.NET eşleşmeyen bir sorgu parametresini sessizce yok sayar: yanlış
// adlı bir filtre hata vermez, sadece HİÇ uygulanmaz — kullanıcı filtrelediğini
// sanırken tüm şirketi görür. (2026-08-11 müşteri geri bildirimi: gender /
// maritalStatus / collarType / employmentType / bloodType `[eq]` eki olmadan
// gönderiliyordu; beşi de tamamen etkisizdi.)
//
// `status` bilerek burada DEĞİL — en sık kullanılan kesit olduğu için şeritteki
// segmente terfi etti.
export const buildFieldDefs = (t) => [
  // Kimlik
  { key: 'firstName[like]', group: t('report.groupIdentity'), label: t('employee.firstName'), kind: 'text' },
  { key: 'lastName[like]', group: t('report.groupIdentity'), label: t('employee.lastName'), kind: 'text' },
  { key: 'corporateEmail[like]', group: t('report.groupIdentity'), label: t('employee.corporateEmail'), kind: 'text' },
  { key: 'registrationNumber[like]', group: t('report.groupIdentity'), label: t('employee.registrationNumber'), kind: 'text' },

  // Organizasyon
  { key: 'departmentId', group: t('report.groupOrg'), label: t('employee.departmentName'), kind: 'lookup', endpoint: '/v1/departments' },
  { key: 'titleId', group: t('report.groupOrg'), label: t('employee.titleName'), kind: 'lookup', endpoint: '/v1/titles' },
  { key: 'gradeId', group: t('report.groupOrg'), label: t('jobGrade.grade'), kind: 'lookup', endpoint: '/v1/job-grades' },
  { key: 'jobFamilyId', group: t('report.groupOrg'), label: t('jobFamily.jobFamily'), kind: 'lookup', endpoint: '/v1/job-families' },
  { key: 'locationId', group: t('report.groupOrg'), label: t('employee.location'), kind: 'lookup', endpoint: '/v1/locations' },
  { key: 'subTenantId', group: t('report.groupOrg'), label: t('employee.subTenant'), kind: 'lookup', endpoint: '/v1/sub-tenants' },

  // Demografi
  { key: 'gender[eq]', group: t('report.groupDemographics'), label: t('employee.gender'), kind: 'pick',
    options: [{ id: 1, name: t('employee.male') }, { id: 2, name: t('employee.female') }, { id: 3, name: t('employee.preferNotToSay') }] },
  { key: 'maritalStatus[eq]', group: t('report.groupDemographics'), label: t('employee.maritalStatus'), kind: 'pick',
    options: [{ id: 1, name: t('employee.married') }, { id: 2, name: t('employee.single') }, { id: 3, name: t('employee.divorced') }] },
  { key: 'numberOfChildren[gte]', group: t('report.groupDemographics'), label: t('employee.numberOfChildren'), kind: 'number', op: '≥' },
  { key: 'numberOfChildren[lte]', group: t('report.groupDemographics'), label: t('employee.numberOfChildren'), kind: 'number', op: '≤' },
  { key: 'disabilityLevel[gte]', group: t('report.groupDemographics'), label: t('employee.disabilityLevel'), kind: 'number', op: '≥' },
  { key: 'nationality[like]', group: t('report.groupDemographics'), label: t('employee.nationality'), kind: 'text' },
  { key: 'bloodType[eq]', group: t('report.groupDemographics'), label: t('employee.bloodType'), kind: 'pick',
    options: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', '0+', '0-'].map(bt => ({ id: bt, name: bt })) },

  // Çalışma
  { key: 'collarType[eq]', group: t('report.groupWork'), label: t('employee.collarType'), kind: 'pick',
    options: [{ id: 1, name: t('employee.whiteCollar') }, { id: 2, name: t('employee.blueCollar') }] },
  { key: 'employmentType[eq]', group: t('report.groupWork'), label: t('employee.employmentType'), kind: 'pick',
    options: [{ id: 1, name: t('employee.fullTime') }, { id: 2, name: t('employee.partTime') }, { id: 3, name: t('employee.contract') }] },

  // Tarihler
  { key: 'employmentDate[gte]', group: t('report.groupDates'), label: t('employee.employmentDate'), kind: 'date', op: '≥' },
  { key: 'employmentDate[lte]', group: t('report.groupDates'), label: t('employee.employmentDate'), kind: 'date', op: '≤' },
  { key: 'dateOfBirth[gte]', group: t('report.groupDates'), label: t('employee.dateOfBirth'), kind: 'date', op: '≥' },
  { key: 'dateOfBirth[lte]', group: t('report.groupDates'), label: t('employee.dateOfBirth'), kind: 'date', op: '≤' },
];

// "Duyuru Yayınla" çekmecesinin şeması. Saf fonksiyon: görsel alanının ayarları
// Duyurular sayfasındaki (pages/announcements/config) alanla BİREBİR aynı olmalı ve
// bu testle kilitlenir — özellikle `purpose`, çünkü onu unutmak sessiz bir kusurdur:
// dosya PRIVATE kovaya yüklenir, duyuru kaydedilir, ama çalışanın kartında görsel
// hiç görünmez ve hiçbir hata basılmaz (bkz. backend StorageVisibility).
export const buildAnnounceSchema = (t) => [
  { name: 'title', label: t('announcement.title', { defaultValue: 'Başlık' }), type: 'text', grid: 12, required: true },
  { name: 'description', label: t('announcement.description', { defaultValue: 'Açıklama' }), type: 'textarea', grid: 12, rows: 4 },
  {
    name: 'imageUrl',
    label: t('announcement.imageUrl', { defaultValue: 'Görsel' }),
    type: 'pictureUpload',
    grid: 12,
    accept: 'image/*',
    validTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    maxSize: 5,
    purpose: 'announcementImage',
  },
  { name: 'sendNotification', label: t('announcement.sendNotification', { defaultValue: 'Bildirim Gönder' }), type: 'checkbox', grid: 12 },
];

// Duyuru OLUŞTURMA isteğinin gövdesi (yayınlama AYRI uçtur). GenericFormDrawer
// `pictureUpload` alanını onSave'den ÖNCE yükleyip File'ı URL'e çevirir, bu yüzden
// burada gelen değer zaten bir URL'dir. Görsel seçilmediyse alan undefined kalır ve
// gövdeye de undefined gider — boş string "görsel var ama bozuk" anlamına gelirdi.
export const buildAnnouncementPayload = (formData) => ({
  title: formData.title,
  description: formData.description,
  imageUrl: formData.imageUrl || undefined,
});

// Toplu izin isteğinin gövdesi. Saf fonksiyon: hedef kimlikler ile form
// alanlarının birleştiği tek yer burası olsun ki testler ekran widget'larını
// sürmek zorunda kalmasın.
//
// `isPaid` ve `cost` GÖNDERİLMEZ (bkz. pages/bulk-leaves/BulkLeavesPage.js
// üstündeki aynı gerekçe): BulkLeaveDataAccess.Create ikisini de istemciden
// okumuyor — ücretlilik İZİN TÜRÜNDEN, maliyet ise kullanıcının çalışma
// takviminden SUNUCUDA hesaplanıyor. Bu çekmece aynı `/v1/bulk-leaves` ucuna
// yazıyordu ve iki alanı da hâlâ taşıyordu; kaydettiğinde sistem farklı bir
// rakam yazıyor ve o rakam ıslak imzalı yıl sonu mutabakat formuna basılıyor.
export const buildBulkLeavePayload = (formData, userIds) => ({
  leaveVariationId: formData.leaveVariationId,
  leaveStart: formData.leaveStart,
  leaveEnd: formData.leaveEnd,
  isHourly: !!formData.isHourly,
  managerNotes: formData.managerNotes || '',
  userIds,
});

const STATUSES = ['active', 'former', 'all'];

// ─── Değer adımı ─────────────────────────────────────────────────

// Alan seçildikten sonraki adım. Üç davranış: hazır seçenek listesi, uzak
// arama (departman/ünvan/lokasyon/alt şirket) ve serbest giriş.
function ValueStep({ field, onPick }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [draft, setDraft] = useState('');
  // Tarih dalı: MUI'nin doğrulama hatası (minDate ihlali = yarım yıl) ve
  // "yalnız bir kez uygula" kilidi. Kilit şart: Enter hem elle karşılanır hem
  // de bazı ortamlarda örtük form gönderimini tetikler; kilit olmasa aynı
  // filtre iki kez eklenirdi.
  const [dateError, setDateError] = useState(null);
  const dateCommitted = useRef(false);

  useEffect(() => {
    if (field.kind !== 'lookup') return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get(field.endpoint, { params });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      if (!cancelled) setResults(items.map(i => ({ id: i.id, name: i.name })));
    }, query ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [field, query]);

  if (field.kind === 'pick') {
    return (
      <PopList>
        {field.options.map(o => (
          <PopItem key={o.id} type="button" onClick={() => onPick(o.id, o.name)}>{o.name}</PopItem>
        ))}
      </PopList>
    );
  }

  if (field.kind === 'lookup') {
    return (
      <>
        <PopSearch>
          <Search size={14} />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('report.searchValue')}
            aria-label={field.label}
          />
        </PopSearch>
        <PopList>
          {results.length === 0
            ? <PopEmpty>{t('report.noResults')}</PopEmpty>
            : results.map(o => (
              <PopItem key={o.id} type="button" onClick={() => onPick(o.id, o.name)}>{o.name}</PopItem>
            ))}
        </PopList>
      </>
    );
  }

  if (field.kind === 'date') {
    // Yazarken UYGULAMA YOK. Yıl bölümüne girilen her hane geçerli bir tarih
    // ürettiğinden (01.01.2 → 0002-01-01) MUI her hane için onChange tetikler;
    // eski sürüm bunu anında filtreye yazıp popover'ı kapatıyordu — seçici
    // DOM'dan kalktığı için yılın kalan haneleri hiç yazılamıyor, filtre
    // 0002-01-01 olarak kalıyordu (müşteri geri bildirimi, 2026-08-31).
    // Uygulama iki yolla olur: takvimden gün seçimi (diğer değer türlerindeki
    // "tıkla-uygula" davranışının aynısı) ya da "Ekle"/Enter.
    // Geçerlilik, çağıranın verdiği `invalid` ile karara bağlanır — state'ten
    // OKUNMAZ. Sebep: MUI onChange ile onAccept'i aynı toplu güncellemede
    // çağırır, arada render yoktur; `dateError`'ı buradan okumak takvim
    // seçiminde bayat (bir önceki keystroke'un) değeri görür ve seçimi sessizce
    // yutar. Yazma yolu state'i kullanır (o yolda render çoktan olmuştur),
    // takvim yolu olayın kendi bağlamını.
    const commitDate = (iso, invalid) => {
      if (dateCommitted.current || !iso || invalid) return;
      dateCommitted.current = true;
      onPick(iso, iso.split('-').reverse().join('.'));
    };
    return (
      <PopForm
        onSubmit={(e) => { e.preventDefault(); commitDate(draft, dateError); }}
        onKeyDown={(e) => {
          // Odak MUI'nin contenteditable bölümünde olduğu için tarayıcı örtük
          // form gönderimi YAPMAZ; Enter'ı elle karşılıyoruz.
          if (e.key === 'Enter') { e.preventDefault(); commitDate(draft, dateError); }
        }}
      >
        <DateTimePicker
          label={field.label}
          mode="date"
          value={draft || null}
          // Alt sınır BİLEREK açık yazıldı: `dateError` bu sınırdan doğuyor,
          // yani yarım yılı (0002) eleyen kural MUI'nin varsayılanına değil
          // bizim kuralımıza dayanıyor.
          minDate="1900-01-01"
          onChange={(iso, ctx) => {
            setDraft(iso || '');
            setDateError(iso ? (ctx?.validationError || null) : 'empty');
          }}
          onAccept={(iso, ctx) => { if (ctx?.source === 'view') commitDate(iso, ctx.validationError); }}
        />
        <Button type="submit" variant="primary" disabled={!draft || !!dateError}>
          {t('common.add')}
        </Button>
      </PopForm>
    );
  }

  return (
    <PopForm
      onSubmit={(e) => {
        e.preventDefault();
        const value = draft.trim();
        if (value === '') return;
        onPick(value, value);
      }}
    >
      <TextInput
        autoFocus
        type={field.kind === 'number' ? 'number' : 'text'}
        label={field.label}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <Button type="submit" variant="primary">{t('common.add')}</Button>
    </PopForm>
  );
}

// ─── Sayfa ───────────────────────────────────────────────────────

export default function EmployeeReportsPage() {
  const { t } = useTranslation();
  const fieldDefs = useMemo(() => buildFieldDefs(t), [t]);

  // Filtre durumu — "Uygula" adımı YOK, seçim anında geçerli.
  const [status, setStatus] = useState('active');
  const [filters, setFilters] = useState([]); // [{ key, value, label }]
  const [page, setPage] = useState({ pageNumber: 1, pageSize: 50 });

  // Alan/değer kutusu
  const [popOpen, setPopOpen] = useState(false);
  const [popField, setPopField] = useState(null);   // null → alan listesi adımı
  const [popEditIdx, setPopEditIdx] = useState(-1); // >= 0 → mevcut çipi düzenle
  const [fieldQuery, setFieldQuery] = useState('');
  const barRef = useRef(null);

  // Tablo
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 50 });
  const [baseCount, setBaseCount] = useState(null);

  // Kolon seçici
  const ALL_COLUMNS = useMemo(() => [
    { key: 'fullName', label: t('employee.fullName'), default: true },
    { key: 'departmentName', label: t('employee.departmentName'), default: true },
    { key: 'titleName', label: t('employee.titleName'), default: true },
    { key: 'locationName', label: t('employee.branch'), default: true },
    { key: 'managerName', label: t('employee.manager'), default: true },
    { key: 'corporateEmail', label: t('employee.corporateEmail'), default: true },
    { key: 'phoneNumber', label: t('employee.phone'), default: false },
    { key: 'personalEmail', label: t('employee.personalEmail'), default: false },
    { key: 'collarType', label: t('employee.collarType'), default: false },
    { key: 'employmentType', label: t('employee.employmentType'), default: false },
    { key: 'employmentDate', label: t('employee.employmentDate'), default: true },
    { key: 'dateOfBirth', label: t('employee.dateOfBirth'), default: false },
    { key: 'registrationNumber', label: t('employee.registrationNumber'), default: false },
    { key: 'citizenId', label: t('employee.citizenId'), default: false },
    { key: 'address', label: t('employee.address'), default: false },
  ], [t]);
  const [selectedCols, setSelectedCols] = useState(() => ALL_COLUMNS.filter(c => c.default).map(c => c.key));
  const [colModalOpen, setColModalOpen] = useState(false);
  const [tempCols, setTempCols] = useState([]);

  // Hızlı aksiyonlar
  const [qaOpen, setQaOpen] = useState(false);
  const qaRef = useRef(null);
  const [activeDrawer, setActiveDrawer] = useState(null); // 'group' | 'announce' | 'email' | 'leave'
  const [targets, setTargets] = useState(null);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [variations, setVariations] = useState([]);

  // ── Sorgu parametreleri — tablo, dışa aktarım ve aksiyonlar TEK kaynaktan
  //    beslenir; ayrışma ihtimali yapısal olarak yok.
  const filterParams = useMemo(() => {
    const params = { status };
    filters.forEach(({ key, value }) => {
      if (value !== null && value !== undefined && value !== '') params[key] = value;
    });
    return params;
  }, [status, filters]);

  const queryParams = useMemo(() => ({ ...filterParams, ...page }), [filterParams, page]);

  const fetchData = useCallback((params) => {
    setFetching(true);
    api.get(`/v1/employees?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || 50,
        });
      })
      .finally(() => setFetching(false));
  }, []);

  useEffect(() => { fetchData(queryParams); }, [queryParams, fetchData]);

  // Sayaçtaki payda: aynı çalışma durumundaki filtresiz toplam. "27 / 57" ancak
  // böyle söylenebilir; tek satır çekmek yeterli.
  useEffect(() => {
    let cancelled = false;
    api.get(`/v1/employees?${new URLSearchParams({ status, pageSize: 1, pageNumber: 1 })}`)
      .then((res) => {
        if (!cancelled) setBaseCount(res.data?.data?.totalRowCount ?? null);
      });
    return () => { cancelled = true; };
  }, [status]);

  // ── Filtre düzenleme
  const resetPage = useCallback(() => setPage(p => ({ ...p, pageNumber: 1 })), []);

  const closePop = useCallback(() => {
    setPopOpen(false);
    setPopField(null);
    setPopEditIdx(-1);
    setFieldQuery('');
  }, []);

  const applyValue = useCallback((value, label) => {
    setFilters((prev) => {
      const entry = { key: popField.key, value, label };
      if (popEditIdx >= 0) return prev.map((f, i) => (i === popEditIdx ? entry : f));
      return [...prev, entry];
    });
    resetPage();
    closePop();
  }, [popField, popEditIdx, resetPage, closePop]);

  const removeFilter = useCallback((idx) => {
    setFilters(prev => prev.filter((_, i) => i !== idx));
    resetPage();
  }, [resetPage]);

  const clearFilters = useCallback(() => {
    setFilters([]);
    resetPage();
  }, [resetPage]);

  const changeStatus = useCallback((next) => {
    setStatus(next);
    resetPage();
  }, [resetPage]);

  // Kutunun dışına tıklayınca kapanır (hızlı aksiyon menüsüyle aynı desen).
  // Tarih filtresinin takvimi MUI Popper'ı ile document.body'ye portal'lanır:
  // mantıken kutunun İÇİ, DOM'da DIŞI. `isInsidePortalLayer` sorulmazsa gün
  // hücresine basılan mousedown kutuyu kapatır, hücre unmount olur ve `click`
  // hiç işlemez — takvimden tarih seçmek İMKÂNSIZ olur (aynı hata puantaj
  // kapsam panelinde de yaşandı, bkz. utils/isInsidePortalLayer).
  useEffect(() => {
    if (!popOpen) return undefined;
    const handler = (e) => {
      if (barRef.current && !barRef.current.contains(e.target) && !isInsidePortalLayer(e.target)) closePop();
    };
    const onKey = (e) => { if (e.key === 'Escape') closePop(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [popOpen, closePop]);

  useEffect(() => {
    const handler = (e) => { if (qaRef.current && !qaRef.current.contains(e.target)) setQaOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePageChange = useCallback((action, tableState) => {
    const parsed = parseTableParams(tableState, []);
    setPage({ pageNumber: parsed.pageNumber || 1, pageSize: parsed.pageSize || 50 });
  }, []);

  // ── Excel
  const handleExportExcel = useCallback(async () => {
    const qs = new URLSearchParams(filterParams).toString();
    try {
      const res = await api.get(`/v1/employees/report/export${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee-report.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* interceptor */ }
  }, [filterParams]);

  // ── Hızlı aksiyon hedefleri
  // Kimlikleri sunucu çözer. Eskiden ekrandaki `data` dizisinden okunuyordu; bu
  // iki hataya yol açıyordu: (1) yalnız görünen sayfa (en çok 50 kişi)
  // kapsanıyordu, (2) `EmployeeResponse` içinde `userId` alanı HİÇ olmadığı için
  // kullanıcı tabanlı aksiyonlar daima BOŞ listeyle gidiyordu.
  const openAction = useCallback(async (name) => {
    setQaOpen(false);
    setActiveDrawer(name);
    setTargets(null);
    setTargetsLoading(true);
    try {
      if (name === 'leave' && variations.length === 0) {
        const vres = await api.get('/v1/leave-variations');
        const r = vres.data?.data;
        setVariations(Array.isArray(r) ? r : (r?.data || []));
      }
      // api.js okuma hatalarında reject ETMEZ — gerçek başarı sinyali res.data.status.
      const res = await api.get(`/v1/employees/report/target-ids?${new URLSearchParams(filterParams)}`);
      setTargets(res.data?.status ? res.data.data : null);
    } finally {
      setTargetsLoading(false);
    }
  }, [filterParams, variations.length]);

  const closeDrawer = useCallback(() => {
    setActiveDrawer(null);
    setTargets(null);
  }, []);

  // GenericFormDrawer onSave(formData)'yı bekler ve truthy dönüşte kapanır.
  // Hedef kümesi çözülmemişse aksiyonu çalıştırmayız: yanlış kümeye
  // uygulamaktansa çekmece açık kalsın ve uyarı görünsün.
  // İKİ ADIM — tek adımlık değil. `POST /v1/user-groups` gövdesi yalnız
  // { name, isAllUsers } (UserGroupRequest.cs); tanımadığı alanı sessizce atar.
  // Eskiden buraya `memberUserIds` konuyordu: grup kuruluyor, üyeler HİÇ
  // eklenmiyordu ve hata da alınmıyordu. Üyeler ayrı uçtan eklenir
  // (AddUsersToGroupRequest.cs → { userIds }).
  const handleCreateGroup = useCallback(async (formData) => {
    if (!targets?.userIds?.length) return false;
    const res = await api.post('/v1/user-groups', { name: formData.name });
    const groupId = res.data?.data?.id;
    // Ad çakışması gibi durumlarda kimlik gelmez; üye eklemeyi denemek 404 üretir.
    if (!groupId) return false;
    await api.post(`/v1/user-groups/${groupId}/users`, { userIds: targets.userIds });
    return true;
  }, [targets]);

  const handlePublishAnnouncement = useCallback(async (formData) => {
    if (!targets?.userIds?.length) return false;
    const res = await api.post('/v1/announcements', buildAnnouncementPayload(formData));
    const id = res.data?.data?.id;
    if (id) {
      await api.post(`/v1/announcements/${id}/publish`, { userIds: targets.userIds, sendNotification: !!formData.sendNotification });
    }
    return true;
  }, [targets]);

  const handleSendEmail = useCallback(async (formData) => {
    if (!targets?.employeeIds?.length) return false;
    await api.post('/v1/employees/report/send-email', { employeeIds: targets.employeeIds, subject: formData.subject, body: formData.body });
    return true;
  }, [targets]);

  const handleBulkLeave = useCallback(async (formData) => {
    if (!targets?.userIds?.length) return false;
    await api.post('/v1/bulk-leaves', buildBulkLeavePayload(formData, targets.userIds));
    return true;
  }, [targets]);

  // ── Çekmece şemaları
  const groupSchema = useMemo(() => [
    { name: 'name', label: t('report.groupName'), type: 'text', grid: 12, required: true },
  ], [t]);

  const announceSchema = useMemo(() => buildAnnounceSchema(t), [t]);

  const emailSchema = useMemo(() => [
    { name: 'subject', label: t('report.emailSubject'), type: 'text', grid: 12, required: true },
    { name: 'body', label: t('report.emailBody'), type: 'textarea', grid: 12, rows: 6 },
  ], [t]);

  const leaveSchema = useMemo(() => [
    { name: 'leaveVariationId', label: t('bulkLeave.leaveType'), type: 'select', grid: 12, required: true,
      options: variations.map(v => ({ id: v.id, name: v.name })) },
    { name: 'leaveStart', label: t('bulkLeave.leaveStart'), type: 'date', grid: 6, required: true },
    // İKİZ ÇEKMECE — gerekçe pages/bulk-leaves/BulkLeavesPage.js'teki aynı
    // alanın üstünde (düzeltme turu 4, Bulgu 1). Aynı `/v1/bulk-leaves` ucuna
    // yazan bu çekmece de bitişi "Bitiş Tarihi" diye etiketliyordu; sunucu ise
    // onu İŞE BAŞLAMA tarihi sayıp maliyeti netliyor.
    { name: 'leaveEnd', type: 'date', grid: 6, required: true,
      label: (f) => (f.isHourly ? t('bulkLeave.leaveEnd') : t('bulkLeave.returnDate')) },
    { name: 'isHourly', label: t('bulkLeave.isHourly'), type: 'checkbox', grid: 6 },
    { name: 'managerNotes', label: t('bulkLeave.managerNotes'), type: 'textarea', grid: 12, rows: 3 },
  ], [t, variations]);

  // `scope` = 'user' → yalnız kullanıcı hesabı olanlara ulaşılabilir (grup,
  // duyuru, izin); 'employee' → kurumsal e-postası olan herkes.
  const renderTargetSummary = (scope) => {
    if (targetsLoading) return <TargetSummary>{t('report.targetLoading')}</TargetSummary>;
    if (!targets) return null;

    const reachable = scope === 'user'
      ? targets.totalCount - targets.withoutUserAccountCount
      : targets.totalCount;

    if (reachable === 0) return <TargetSummary $warn>{t('report.targetEmpty')}</TargetSummary>;

    return (
      <TargetSummary>
        {t('report.targetScope', { count: reachable })}
        {scope === 'user' && targets.withoutUserAccountCount > 0 && (
          <TargetWarning>{t('report.targetNoAccount', { count: targets.withoutUserAccountCount })}</TargetWarning>
        )}
      </TargetSummary>
    );
  };

  // ── Kolonlar
  const columnDefs = useMemo(() => ({
    fullName: {
      name: 'fullName', label: t('employee.fullName'),
      options: { filter: false, sort: true, customBodyRender: (_, tableMeta) => {
        const row = data[tableMeta.rowIndex];
        return row ? <PersonCell name={`${row.firstName} ${row.lastName}`} profilePicture={row.profilePicture} /> : null;
      }},
    },
    departmentName: { name: 'departmentName', label: t('employee.departmentName'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    titleName: { name: 'titleName', label: t('employee.titleName'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    locationName: { name: 'locationName', label: t('employee.branch'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    managerName: { name: 'managerName', label: t('employee.manager'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    corporateEmail: { name: 'corporateEmail', label: t('employee.corporateEmail'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    personalEmail: { name: 'personalEmail', label: t('employee.personalEmail'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    phoneNumber: { name: 'phoneNumber', label: t('employee.phone'), options: { filter: false, customBodyRender: (_, tableMeta) => {
      const row = data[tableMeta.rowIndex];
      return formatPhone(row?.areaCode, row?.phoneNumber);
    }}},
    collarType: { name: 'collarType', label: t('employee.collarType'), options: { filter: false, customBodyRender: (v) => v === 1 ? t('employee.whiteCollar') : v === 2 ? t('employee.blueCollar') : '-' } },
    employmentType: { name: 'employmentType', label: t('employee.employmentType'), options: { filter: false, customBodyRender: (v) => v === 1 ? t('employee.fullTime') : v === 2 ? t('employee.partTime') : v === 3 ? t('employee.contract') : '-' } },
    employmentDate: { name: 'employmentDate', label: t('employee.employmentDate'), options: { filter: false, customBodyRender: (v) => v ? v.substring(0, 10) : '-' } },
    dateOfBirth: { name: 'dateOfBirth', label: t('employee.dateOfBirth'), options: { filter: false, customBodyRender: (v) => v ? v.substring(0, 10) : '-' } },
    registrationNumber: { name: 'registrationNumber', label: t('employee.registrationNumber'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    citizenId: { name: 'citizenId', label: t('employee.citizenId'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    address: { name: 'address', label: t('employee.address'), options: { filter: false, customBodyRender: (v) => v || '-' } },
  }), [t, data]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    ...selectedCols.map(key => columnDefs[key]).filter(Boolean),
  ], [selectedCols, columnDefs]);

  // ── Alan listesi (eklenmiş alanlar hariç, aramaya göre süzülü, gruplu)
  const availableFields = useMemo(() => {
    const used = filters.map(f => f.key);
    const q = fieldQuery.trim().toLocaleLowerCase('tr');
    return fieldDefs
      .filter(fd => !used.includes(fd.key))
      .filter(fd => !q || `${fd.label} ${fd.op || ''}`.toLocaleLowerCase('tr').includes(q));
  }, [fieldDefs, filters, fieldQuery]);

  const statusLabel = {
    active: t('employee.active'),
    former: t('employee.formerEmployees'),
    all: t('common.all'),
  };

  const isFiltered = filters.length > 0;

  return (
    <Container>
      <Bar ref={barRef}>
        <Segment role="group" aria-label={t('report.employmentStatus')}>
          {STATUSES.map(s => (
            <SegmentBtn
              key={s}
              type="button"
              $active={status === s}
              aria-pressed={status === s}
              onClick={() => changeStatus(s)}
            >
              {statusLabel[s]}
            </SegmentBtn>
          ))}
        </Segment>

        <Divider aria-hidden="true" />

        <Chips>
          {filters.map((f, idx) => {
            const fd = fieldDefs.find(d => d.key === f.key);
            if (!fd) return null;
            return (
              <Chip key={f.key}>
                <ChipMain
                  type="button"
                  aria-label={`${fd.label} — ${t('report.editFilter')}`}
                  onClick={() => { setPopField(fd); setPopEditIdx(idx); setPopOpen(true); }}
                >
                  <span className="k">{fd.label}{fd.op ? ` ${fd.op}` : ''}</span>
                  <span className="v">{f.label}</span>
                </ChipMain>
                <ChipRemove
                  type="button"
                  aria-label={`${fd.label} — ${t('report.removeFilter')}`}
                  onClick={() => removeFilter(idx)}
                >
                  <X size={13} />
                </ChipRemove>
              </Chip>
            );
          })}
        </Chips>

        <AddFilterBtn
          type="button"
          aria-haspopup="dialog"
          aria-expanded={popOpen}
          onClick={() => (popOpen ? closePop() : (setPopField(null), setPopEditIdx(-1), setPopOpen(true)))}
        >
          <Plus size={13} /> {t('report.addFilter')}
        </AddFilterBtn>

        <BarRight>
          <Count $filtered={isFiltered}>
            {isFiltered
              ? <><b>{pagination.totalRowCount}</b>{baseCount !== null ? ` / ${baseCount}` : ''} {t('report.employeeUnit')}</>
              : <><b>{baseCount !== null ? baseCount : pagination.totalRowCount}</b> {t('report.employeeUnit')}</>}
          </Count>

          {isFiltered && (
            <ClearBtn type="button" onClick={clearFilters}>{t('report.clear')}</ClearBtn>
          )}

          <Tooltip text={t('report.selectColumns')}>
            <IconButton
              type="button"
              size="sm"
              aria-label={t('report.selectColumns')}
              onClick={() => { setTempCols([...selectedCols]); setColModalOpen(true); }}
            >
              <Columns3 size={15} />
            </IconButton>
          </Tooltip>

          <Tooltip text={t('report.exportExcel')}>
            <IconButton type="button" size="sm" aria-label={t('report.exportExcel')} onClick={handleExportExcel}>
              <Download size={15} />
            </IconButton>
          </Tooltip>

          <DropdownWrapper ref={qaRef}>
            <Tooltip text={t('report.quickActions')}>
              <IconButton
                type="button"
                size="sm"
                variant="gradient"
                aria-label={t('report.quickActions')}
                onClick={() => setQaOpen(o => !o)}
              >
                <Zap size={15} />
              </IconButton>
            </Tooltip>
            {qaOpen && (
              <DropdownMenu>
                <DropdownItem onClick={() => openAction('group')}>{t('report.createGroup')}</DropdownItem>
                <DropdownItem onClick={() => openAction('announce')}>{t('report.publishAnnouncement')}</DropdownItem>
                <DropdownItem onClick={() => openAction('email')}>{t('report.sendEmail')}</DropdownItem>
                <DropdownItem onClick={() => openAction('leave')}>{t('report.bulkLeave')}</DropdownItem>
              </DropdownMenu>
            )}
          </DropdownWrapper>
        </BarRight>

        {popOpen && (
          <Popover role="dialog" aria-label={t('report.addFilter')}>
            {popField ? (
              <>
                <PopHeader>
                  <PopBack type="button" aria-label={t('common.back')} onClick={() => { setPopField(null); setPopEditIdx(-1); }}>
                    <ChevronLeft size={16} />
                  </PopBack>
                  <span>{popField.label}{popField.op ? ` ${popField.op}` : ''}</span>
                </PopHeader>
                <ValueStep field={popField} onPick={applyValue} />
              </>
            ) : (
              <>
                <PopSearch>
                  <Search size={14} />
                  <input
                    autoFocus
                    value={fieldQuery}
                    onChange={(e) => setFieldQuery(e.target.value)}
                    placeholder={t('report.searchField')}
                    aria-label={t('report.searchField')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && availableFields.length > 0) setPopField(availableFields[0]);
                    }}
                  />
                </PopSearch>
                <PopList>
                  {availableFields.length === 0 ? (
                    <PopEmpty>{fieldQuery ? t('report.noFieldMatch') : t('report.allFieldsAdded')}</PopEmpty>
                  ) : (
                    availableFields.map((fd, i) => (
                      <React.Fragment key={fd.key}>
                        {(i === 0 || availableFields[i - 1].group !== fd.group) && <PopGroup>{fd.group}</PopGroup>}
                        <PopItem type="button" onClick={() => setPopField(fd)}>
                          {fd.label}{fd.op ? ` ${fd.op}` : ''}
                        </PopItem>
                      </React.Fragment>
                    ))
                  )}
                </PopList>
              </>
            )}
          </Popover>
        )}
      </Bar>

      <Modal open={colModalOpen} onClose={() => setColModalOpen(false)}>
        <ColModalWrap>
          <ColModalTitle>{t('report.selectColumns')}</ColModalTitle>
          {ALL_COLUMNS.map(col => (
            <ColCheckRow key={col.key}>
              <input
                type="checkbox"
                checked={tempCols.includes(col.key)}
                onChange={(e) => {
                  if (e.target.checked) setTempCols(prev => [...prev, col.key]);
                  else setTempCols(prev => prev.filter(k => k !== col.key));
                }}
              />
              {col.label}
            </ColCheckRow>
          ))}
          <ColModalActions>
            <Button variant="outline" onClick={() => setColModalOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" onClick={() => { setSelectedCols(tempCols); setColModalOpen(false); }}>{t('common.save')}</Button>
          </ColModalActions>
        </ColModalWrap>
      </Modal>

      <DataTable
        title={t('report.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onTableChanged={handlePageChange}
      />

      <GenericFormDrawer
        open={activeDrawer === 'group'}
        onClose={closeDrawer}
        title={t('report.createGroup')}
        schema={groupSchema}
        onSave={handleCreateGroup}
      >
        {renderTargetSummary('user')}
      </GenericFormDrawer>

      <GenericFormDrawer
        open={activeDrawer === 'announce'}
        onClose={closeDrawer}
        title={t('report.publishAnnouncement')}
        schema={announceSchema}
        onSave={handlePublishAnnouncement}
      >
        {renderTargetSummary('user')}
      </GenericFormDrawer>

      <GenericFormDrawer
        open={activeDrawer === 'email'}
        onClose={closeDrawer}
        title={t('report.sendEmail')}
        schema={emailSchema}
        onSave={handleSendEmail}
      >
        {renderTargetSummary('employee')}
      </GenericFormDrawer>

      <GenericFormDrawer
        open={activeDrawer === 'leave'}
        onClose={closeDrawer}
        title={t('report.bulkLeave')}
        schema={leaveSchema}
        onSave={handleBulkLeave}
      >
        {renderTargetSummary('user')}
      </GenericFormDrawer>
    </Container>
  );
}
